// Pure posology logic — no browser/React imports

// ── Posology units helpers (inlined from posology-units.ts) ──────────────────

type CalendarUnitFamily = "hour" | "day" | "week" | "month";

const PT_UNITS: Record<CalendarUnitFamily, { singular: string; plural: string }> = {
  hour: { singular: "hora", plural: "horas" },
  day: { singular: "dia", plural: "dias" },
  week: { singular: "semana", plural: "semanas" },
  month: { singular: "mês", plural: "meses" },
};

const EN_UNITS: Record<CalendarUnitFamily, { singular: string; plural: string }> = {
  hour: { singular: "hour", plural: "hours" },
  day: { singular: "day", plural: "days" },
  week: { singular: "week", plural: "weeks" },
  month: { singular: "month", plural: "months" },
};

function stripDiacritics(input: string): string {
  return input.normalize("NFD").replace(/\p{M}/gu, "");
}

function detectCalendarUnitFamily(raw: string): CalendarUnitFamily | null {
  const u = stripDiacritics(raw.trim().toLowerCase());
  if (["hora", "horas", "hour", "hours", "h"].includes(u)) return "hour";
  if (["dia", "dias", "day", "days"].includes(u)) return "day";
  if (["semana", "semanas", "week", "weeks"].includes(u)) return "week";
  if (["mes", "meses", "month", "months"].includes(u)) return "month";
  return null;
}

function parsePosologyCount(raw: string | null | undefined): number | null {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return null;
  const normalized = trimmed.replace(",", ".");
  const n = Number.parseFloat(normalized);
  return Number.isFinite(n) ? n : null;
}

function isSingularCount(n: number): boolean {
  return Math.abs(n - 1) < 1e-9;
}

export function normalizeDoseMeasureUnit(raw: string): string {
  return raw.trim().toLowerCase();
}

export function resolveCalendarUnitForCount(
  rawUnit: string,
  countStr: string | null | undefined,
  language: string,
): string {
  const trimmed = rawUnit.trim();
  if (!trimmed) return "";

  const family = detectCalendarUnitFamily(trimmed);
  if (!family) {
    return trimmed.toLowerCase();
  }

  const count = parsePosologyCount(countStr ?? "");
  const usePlural = count == null ? true : !isSingularCount(count);
  const isPt = language.toLowerCase().startsWith("pt");
  const forms = isPt ? PT_UNITS[family] : EN_UNITS[family];
  return usePlural ? forms.plural : forms.singular;
}

// ── Posology types and helpers ───────────────────────────────────────────────

export type PosologyMode = "automatic" | "free_text";

export interface PosologyItemFields {
  posology_mode?: string | null;
  free_text_posology?: string | null;
  doseAmount?: string | null;
  doseUnit?: string | null;
  frequencyValue?: string | null;
  frequencyUnit?: string | null;
  durationValue?: string | null;
  durationUnit?: string | null;
  // Legacy snake_case aliases (from DB rows)
  dose_amount?: string | null;
  dose_unit?: string | null;
  frequency_value?: string | null;
  frequency_unit?: string | null;
  duration_value?: string | null;
  duration_unit?: string | null;
}

export function normalizePosologyText(value: string | null | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}

function humanizeLegacyPosologyText(
  value: string,
  t: (subKey: string, options?: Record<string, unknown>) => string,
): string {
  const normalized = normalizePosologyText(value)
    .replace(/^[^:]+:\s*/i, (match) => (/\bfrequência\b|\bduração\b/i.test(match) ? match : ""))
    .trim();

  const structuredMatch = normalized.match(
    /^([^.]+?)(?:\.\s*|\s+)Frequência:\s*([^.]+?)(?:\.\s*|\s+)Duração:\s*([^.]+?)(?:\.\s*(.+))?$/i,
  );

  if (!structuredMatch) {
    return normalized;
  }

  const [, legacyPresentation, frequency, duration, extra] = structuredMatch;
  const sentence = `${t("presentation", { defaultValue: "Apresentação" })}: ${legacyPresentation.trim()}. Dar a cada ${frequency.trim()} por ${duration.trim()}`;
  const suffix = normalizePosologyText(extra);
  return suffix ? `${sentence}. ${suffix}` : sentence;
}

export function getPosologyMode(
  item: Pick<PosologyItemFields, "posology_mode" | "free_text_posology">,
): PosologyMode {
  if (item.posology_mode === "free_text") return "free_text";
  if (item.posology_mode === "automatic") return "automatic";
  return normalizePosologyText(item.free_text_posology) ? "free_text" : "automatic";
}

export function isFreeTextPosologyItem(item: PosologyItemFields): boolean {
  return getPosologyMode(item) === "free_text";
}

function resolvePosologyField(camel: string | null | undefined, snake: string | null | undefined): string {
  return normalizePosologyText(camel ?? snake);
}

export function buildAutomaticPosologyDescription(
  item:
    | Pick<
        PosologyItemFields,
        | "doseAmount"
        | "doseUnit"
        | "frequencyValue"
        | "frequencyUnit"
        | "durationValue"
        | "durationUnit"
        | "dose_amount"
        | "dose_unit"
        | "frequency_value"
        | "frequency_unit"
        | "duration_value"
        | "duration_unit"
      >
    | null
    | undefined,
  t: (subKey: string, options?: Record<string, unknown>) => string,
  lang = "pt",
): string {
  if (!item) return "";

  const doseAmount = resolvePosologyField((item as PosologyItemFields).doseAmount, (item as PosologyItemFields).dose_amount);
  const doseUnitRaw = resolvePosologyField((item as PosologyItemFields).doseUnit, (item as PosologyItemFields).dose_unit);
  const doseUnit = doseUnitRaw ? normalizeDoseMeasureUnit(doseUnitRaw) : "";
  const frequencyValue = resolvePosologyField((item as PosologyItemFields).frequencyValue, (item as PosologyItemFields).frequency_value);
  const frequencyUnitRaw = resolvePosologyField((item as PosologyItemFields).frequencyUnit, (item as PosologyItemFields).frequency_unit);
  const durationValue = resolvePosologyField((item as PosologyItemFields).durationValue, (item as PosologyItemFields).duration_value);
  const durationUnitRaw = resolvePosologyField((item as PosologyItemFields).durationUnit, (item as PosologyItemFields).duration_unit);

  const frequencyUnit = frequencyUnitRaw
    ? resolveCalendarUnitForCount(frequencyUnitRaw, frequencyValue, lang)
    : "";
  const durationUnit = durationUnitRaw
    ? resolveCalendarUnitForCount(durationUnitRaw, durationValue, lang)
    : "";

  const dosePart = [doseAmount, doseUnit].filter(Boolean).join(" ");
  const frequencyPart =
    frequencyValue && frequencyUnit
      ? t("posologyEvery", {
          defaultValue: "a cada {{value}} {{unit}}",
          value: frequencyValue,
          unit: frequencyUnit,
        })
      : "";
  const durationPart =
    durationValue && durationUnit
      ? t("posologyFor", {
          defaultValue: "por {{value}} {{unit}}",
          value: durationValue,
          unit: durationUnit,
        })
      : "";

  if (!dosePart && !frequencyPart && !durationPart) {
    return "";
  }

  return t("posologyAutomaticSentence", {
    defaultValue: "Dar {{dose}}{{frequency}}{{duration}}",
    dose: dosePart || t("posologyDoseFallback", { defaultValue: "conforme orientação" }),
    frequency: frequencyPart ? ` ${frequencyPart}` : "",
    duration: durationPart ? ` ${durationPart}` : "",
  }).trim();
}

export function buildPosologySummary(
  item: PosologyItemFields,
  t: (subKey: string, options?: Record<string, unknown>) => string,
  lang = "pt",
): string {
  if (isFreeTextPosologyItem(item)) {
    return humanizeLegacyPosologyText(normalizePosologyText(item.free_text_posology), t);
  }
  return buildAutomaticPosologyDescription(item, t, lang);
}

export function buildMedicationTitleLine(
  medicationName: string | null | undefined,
  _presentation: string | null | undefined,
  quantity: string | null | undefined,
): string {
  const name = normalizePosologyText(medicationName) || "—";
  const q = normalizePosologyText(quantity);
  return [name, q].filter(Boolean).join(" - ");
}
